import React, { FC, useState, useRef, useEffect } from 'react';
import styles from './index.module.less';
import { activeDataType } from '../../type';
import {
  queryProcessComplete,
  getBaseRecordList,
} from './../../effects/process.service';
import { message, Modal, Input, Button, Upload, Space } from 'antd';
import { DownCircleFilled } from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { Api as FetchAPI } from '@cui/req';
import {
  getPolicy,
  policyUpload,
  upload,
} from '@/common/asset-apis/assetModel';
import useMetaStore from '@/store/useMetaStore';
import {
  ApproverSettingForm,
  ModalLayout,
} from '@/asset/AssetTask/templates/Cascade/components';
import { cloneDeep } from 'lodash';
import useValuesStore from '@/store/useValuesStore';
import { getJsonAta } from '@/utils';
import {
  UserInfo,
  getUserByUserAccounts,
  getUserInfoCache,
} from '@/api/system';
import { useAsyncEffect } from 'ahooks';
import {
  ASSIGNEE_ROLE_SCOPE,
  ASSIGNEE_TYPE,
  NeedAssignNode,
  ProcessTemplateNode,
} from '@/types/process';
import RuleController from '@/components/RuleController';
import { useSaveEvent } from '@/components/SPCascadeBtn/effect';

const { TextArea } = Input;
// const { Dragger } = Upload

export const ApprovalForm: FC<activeDataType> = ({
  taskId,
  nodeId,
  reload,
  onSuccess,
  onFail,
  approverSchema,
  urlTaskId,
  refList,
}) => {
  const { values, realoadValue, pageRules } = useValuesStore();
  const { isMobile } = useMetaStore();
  const [comment, setComment] = useState('');
  const [fileList, setFileList] = useState<any[]>([]);
  const [attachmentsList, setAttachmentsList] = useState<any[]>([]);
  const uploadHost = useRef<string>('');
  const uploadUrl = useRef<string>('');
  // const [isModalOpen, setIsModalOpen] = useState(false)
  // const showModal = () => {
  //     setIsModalOpen(true);
  // };

  // const handleCancel = () => {
  //     setIsModalOpen(false);
  // };

  // 审批设置
  const [approverData, setApproverData] = useState<any>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [okRules, setOkRules] = useState<any>();
  const [ruleMessage, setRuleMessage] = useState<any>();
  const approverSettingFormRef = useRef<any>();
  const [approverValue, setApproverValue] = useState<any>({});
  // 选中的审批人姓名
  const [approverNames, setApproverNames] = useState<string[]>([]);

  const getApproverSchema = async () => {
    // 审批流程配置
    const nodeRes = await getBaseRecordList({
      appId: 'tech',
      modelId: 'approval_design',
      data: {
        condition: 'relation_task_id = ${relation_task_id}',
        parameters: { relation_task_id: urlTaskId },
      },
    });

    const approvalProcessDesign = nodeRes?.data?.[0];
    const nodeList: ProcessTemplateNode[] =
      approvalProcessDesign?.schema_values?.nodes || [];

    approverSchema?.needAssignNodes?.forEach((item: NeedAssignNode) => {
      const nodeItem = nodeList.find((el: any) => el.id === item.assigned_node);
      const basicConfiguration = nodeItem?.data?.basicConfiguration;
      if (basicConfiguration) {
        item.nodeName = basicConfiguration.name;
        item.assigneeFilterRole = basicConfiguration.assigneeFilterRole;
        item.assigneeRoleScope = basicConfiguration.assigneeRoleScope;
        item.ccFilterRole = basicConfiguration.ccFilterRole;
      }
    });
    setApproverData(cloneDeep(approverSchema));
  };
  const getOKRules = async () => {
    const nodeRes = await getBaseRecordList({
      appId: 'tech',
      modelId: 'approval_design',
      data: {
        condition: 'relation_task_id = ${relation_task_id}',
        parameters: { relation_task_id: urlTaskId },
      },
    });
    const nodeList = nodeRes?.data?.[0]?.schema_values?.nodes || [];
    const node = nodeList.find((item) => item.id === nodeId);
    setOkRules(node?.data?.basicConfiguration?.onOkRule);
    setRuleMessage(node?.data?.basicConfiguration?.ruleMessage);
  };

  useEffect(() => {
    if (urlTaskId) {
      getOKRules();
    }
  }, [taskId]);

  useEffect(() => {
    if (urlTaskId && approverSchema?.needAssign) {
      getApproverSchema();
    } else {
      setApproverData(cloneDeep(approverSchema));
    }
    setApproverValue(undefined);
  }, [taskId, approverSchema]);

  useAsyncEffect(async () => {
    if (!approverValue) {
      setApproverNames([]);
      return;
    }
    const node = approverSchema.needAssignNodes.find(
      (item) => item.node === nodeId,
    );
    if (!node) return;

    const assignees = approverValue[node.assigned_node];
    if (assignees?.assignee && Array.isArray(assignees.assignee)) {
      const users = await getUserByUserAccounts(assignees.assignee as string[]);
      setApproverNames(users.map((user) => user.userName));
    }
  }, [approverValue]);

  const { pureSave } = useSaveEvent({ refList });

  const approvalComplete = async (type: number) => {
    if (type === 0 && !comment) {
      message.info('请输入审批意见');
      return;
    }
    if (approverData?.needAssign && !approverValue && type === 1) {
      message.info('请设置审批人');
      return;
    }
    if (okRules !== null) {
      let res = '';
      try {
        res = getJsonAta(okRules.ruleExp).evaluate(values);
      } catch (e) {
        console.warn(e);
      }
      if (res) {
        message.info(ruleMessage);
        return;
      }
    }
    const attachments = attachmentsList?.map((fileItem: any) => {
      return {
        attachmentName: fileItem.name,
        url: fileItem.url,
        attachmentType: '',
      };
    });
    const params: any = {
      auditComment: {
        activityNodeName: '',
        attachments,
        auditRemark: comment,
        auditTime: '',
        auditType: type,
      },
      taskId,
      variables: {},
    };
    if (approverValue) {
      params.approverSetData = approverValue;
    }
    Modal.confirm({
      title: `确认${type === 0 ? '驳回' : '同意'}`,
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        try {
          const success = await pureSave('save-btns');
          if (!success) {
            throw new Error('保存失败');
          }
          const res = await queryProcessComplete(params);
          if (res.success) {
            reload && reload();
            onSuccess && onSuccess(params, res);
          } else {
            onFail && onFail(params, res);
          }
        } catch (err) {
          onFail && onFail(params, err);
        }
      },
    });
  };

  const handleUpload = (file: any) => {
    // // 获取签名
    // getPolicy(FetchAPI, file.file.name).then((res) => {
    //   const { xamzAlgorithm, xamzCredential, policy, xamzSignature, xamzDate, host } = res;
    //   uploadHost.current = host;
    //   const formData = new FormData();
    //   formData.append('key', file.file.name);
    //   formData.append('x-amz-algorithm', xamzAlgorithm);
    //   formData.append('x-amz-credential', xamzCredential);
    //   formData.append('policy', policy);
    //   formData.append('x-amz-signature', xamzSignature);
    //   formData.append('x-amz-date', xamzDate);
    //   formData.append('file', file.file);
    //   // 上传文件
    //   policyUpload(FetchAPI, host, formData).then(() => {
    //     file.onProgress({ percent: 100 });
    //     file.onSuccess();
    //   });
    // });

    upload(FetchAPI, file).then((res) => {
      uploadUrl.current = res;
      file.onProgress({ percent: 100 });
      file.onSuccess();
    });
  };

  const changeAttachmentsList = (list: any[]) => {
    const newResList = (list || [])
      .filter((el: any) => el)
      .map((el: any) => {
        return {
          name: el.name,
          url: uploadUrl.current,
        };
      });
    setAttachmentsList([...newResList]);
  };

  const handleChange: UploadProps['onChange'] = (info: any) => {
    switch (info.file.status) {
      case 'done':
        changeAttachmentsList(info.fileList);
        break;
      case 'removed':
        changeAttachmentsList(info.fileList);
        break;
      default:
        break;
    }
    setFileList([...info.fileList]);
  };

  const uploadProps: UploadProps = {
    action: '#',
    onChange: handleChange,
    customRequest: handleUpload,
    fileList,
    multiple: true,
    // maxCount: 1,
    // name: 'upload',
  };

  const openModal = () => {
    setIsModalOpen(true);
  };

  return (
    <>
      <div
        className={
          isMobile ? styles['approval-form-mobile'] : styles['approval-form']
        }
      >
        {approverData?.needAssign && (
          <div className={styles['approval-set-box']}>
            <Button onClick={openModal}>设置审批人</Button>
            {approverValue && (
              <div className={styles.approver}>
                <DownCircleFilled
                  style={{ color: '#23B066', marginRight: '4px' }}
                />
                <span>已设置</span>
                {approverNames.length > 0 && (
                  <span>：{approverNames.join(',')}</span>
                )}
              </div>
            )}
          </div>
        )}
        <TextArea
          rows={4}
          placeholder="请输入审批意见"
          onChange={(val) => {
            setComment(val.target.value);
          }}
          value={comment}
        />
        <div className={styles['approval-form-upload']}>
          <Upload {...uploadProps}>
            <Button>上传附件</Button>
          </Upload>
        </div>
        <div className={styles['approval-form-complete']}>
          <Space>
            <RuleController id="reject-btns" key="reject-btns">
              <Button
                type="primary"
                danger
                ghost
                onClick={() => approvalComplete(0)}
              >
                驳回
              </Button>
            </RuleController>
            <Button type="primary" onClick={() => approvalComplete(1)}>
              同意
            </Button>
          </Space>
        </div>
      </div>
      <ModalLayout
        title="审批设置"
        visible={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={async () => {
          try {
            await approverSettingFormRef.current?.form?.validateFields();
          } catch (e) {
            console.error(e);
            message.error('请填写必选项');
            return;
          }
          const approverSetData = approverSettingFormRef.current?.form?.getFieldsValue();
          setApproverValue(approverSetData);
          setIsModalOpen(false);
        }}
        width={800}
        destroyOnClose
        position="top"
      >
        <ApproverSettingForm
          approverSchema={approverData}
          ref={approverSettingFormRef}
          assigneeNode={approverValue}
        />
      </ModalLayout>
    </>
  );
};
export default ApprovalForm;
